/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {SelectableOptions} from '../../../models/selectable-options';
import {ExcelExportService} from '../../../services/excel-export.service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-save-global-vehicle-template',
    templateUrl: './save-global-vehicle-template.component.html',
    styleUrl: './save-global-vehicle-template.component.scss',
    standalone: false
})
export class SaveGlobalVehicleTemplateComponent {

  showSpinner: boolean = false;
  file: File;
  sheets: SelectableOptions = new SelectableOptions();


  constructor(public readonly excelService: ExcelExportService,
              public readonly vehicleScenarioService: VehicleScenarioService,
              public readonly stateService: StateService,
              public snackBar: FabMessageService,
              public dialog: MatDialog) {
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }


  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets.options,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheets.selectedOptions = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.sheets.selectedOptions.length === 0) {
          this.file = null;
          this.snackBar.displayError("Please re-upload file", DISMISS_MSG)
        } else {
          this.saveVehicleForecast();
        }
      }
    );
  }

  /**
   * Saves the global vehicle forecast file in the database
   */
  saveVehicleForecast(): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Import',
        message: 'The following import will update data. Are you sure you want to import the file?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'warning',
        actionColor: 'primary'
      },
      disableClose: true
    });
    dialogRef.componentInstance.yes.subscribe(() => {
      this.showSpinner = true;
      this.vehicleScenarioService.saveGlobalVehicleForecast(this.file, this.sheets.selectedOptions[0], this.stateService.getUserGmid()).subscribe(
        () => {
          this.showSpinner = false;
          this.snackBar.displaySuccess('File imported successfully', DISMISS_MSG);
        },
        error => {
          this.showSpinner = false;
          this.snackBar.displayError(error, DISMISS_MSG);
        }
      );
    });
  }
}
